import './track-order.css';

import { Util } from "../../../utils/util";
import { OrderStatus, OrderType, Pages, ContentType, MapProvider, DeliveryProvider } from "../../../types/enums";
import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { User } from '../../../models/user';
import { Page } from '../page';
import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { TrackerService } from "../../../services/tracker-service";
import { DataLayerService } from '../../../services/data-layer-service';
import { TrackOrderState, TrackOrderModules } from "../../../services/tracker-service.interface";
import { DialogCreators } from '../../../utils/dialog-creators';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { Router } from '../../../core/router';
import { ConfiguredMapProvider } from '../../../services/maps/configured-map-provider';
import { MapMarkerInfoWindow } from '../../shared/MapMarkerInfoWindow/map-marker-info-window';
import { BoundsRect, IMapMarker, LatLng } from '../../../services/maps/map-provider.interface';

export interface ITrackOrderProps {
    orderId: number,
    fromRecentOrdersPage?: boolean
}

export class TrackOrder extends Page {
    private readonly _trackerService: TrackerService;
    private readonly _dataLayer: DataLayerService;
    private readonly _order: Order;
    private readonly _user: User;
    private readonly _item: TrackOrderState;
    private readonly _modules: TrackOrderModules;
    private _options?: ITrackOrderProps;
    private _trackerTimeoutInterval: NodeJS.Timeout | null = null;
    private _isRendered: boolean = false;
    private readonly _configuredMapProvider: ConfiguredMapProvider;

    private _vehicleMarker: IMapMarker | null = null;
    private _vehicleRefreshTimer: NodeJS.Timeout | null = null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, user: User,
        trackerService: TrackerService, dataLayer: DataLayerService) {
        super("page_trackorder", eventBus, data, context, localizationService);
        this._trackerService = trackerService;
        this._dataLayer = dataLayer;
        this._order = order;
        this._user = user;
        this._item = this._initialModel();
        this._configuredMapProvider = new ConfiguredMapProvider(data, context, MapProvider.HERE);
        this._modules = this._initialModules();
        this._eventBusListeners();
    }

    /**
     * Initializes the model with default values.
     * @returns
     */
    private _initialModel(): TrackOrderState {
        return {
            order: {
                address: null,
                firstName: "",
                id: 0,
                orderTypeId: OrderType.TAKE_OUT,
                lastName: "",
                number: 0,
                phone: "",
                deliveryProviderId: DeliveryProvider.NONE,
                deliveryCount: 0,
                status: {
                    estimatedDeliveryTime: null,
                    estimatedPromiseTime: null,
                    printTime: null,
                    statusesInfo: [],
                    trackerStatusOK: false
                }
            },
            storeAddressInfo: {
                name: "",
                streetNumber: "",
                address1: "",
                address2: "",
                address3: "",
                address4: "",
                address5: "",
                city: "",
                state: "",
                zip: "",
                countryId: 0,
                latitude: 0,
                longitude: 0,
                phone: "",
                storeKey: ""
            },
            oldOrderInfo: {
                deliveryProviderId: DeliveryProvider.NONE,
                deliveryCount: 0
            }
        };
    }

    private _initialModules(): TrackOrderModules {
        return {
            mapProvider: this._configuredMapProvider.provider,
            mapInstance: null,
            mapMarkerInfoWindow: new MapMarkerInfoWindow(this._data, this._localizationService)
        };
    }

    private _setMapProvider(): void {
        if (!this._configuredMapProvider.refresh())
            return;

        this._modules.mapProvider = this._configuredMapProvider.provider;
        this._modules.mapInstance = null;
    }

    public hide(): void {
        this._clearPage();
        super.hide();
    }

    /**
     * Initializes the component asynchronously.
     */
    public async initAsync(): Promise<void> {
        if (this._data) {
            this._item.order.id = this._order.getProperty("OrderID") || 0;
        }

        if (this._item.order.id > 0) {
            await this.updateTrackerAsync();
        }
    }

    /**
     * Sets event bus listeners when the page location has changed
     */
    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.HEADER_CANCEL, () => {
            if (Router.path !== Pages.TRACK_ORDER)
                return;

            this._clearPage();

            if (this._options?.fromRecentOrdersPage)
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ORDERS);
            else
                this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
        });

        this._eventBus.on(EventTypes.LOCATION_CHANGE, async (page: Pages, options?: ITrackOrderProps) => {
            if (page !== Pages.TRACK_ORDER)
                return;

            this._options = options;
            await this._onLocationChangeAsync(this._options?.orderId ?? 0);
        });

        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, async () => {
            this._setMapProvider();

            if (Router.path === Pages.TRACK_ORDER)
                await this._onLocationChangeAsync(this._item.order.id);
        });
    }

    /**
     * Sets event listeners and the auto refresh interval for the tracker page.
     */
    private readonly _setEventListeners = (): void => {
        const storePhoneNumber = this._formatPhoneNumber();
        if (storePhoneNumber) {
            const button = document.getElementById("btn_call_store");
            if (button) {
                button.onclick = () => {
                    if (typeof openPhone == "function")
                        openPhone(storePhoneNumber);

                    if (this._dataLayer)
                        this._dataLayer.track_order_connect(ContentType.CALL, this._data.getProperty("StoreInfo").storeCode.toString());
                };
            }
        }

        const storeLocation = this._getStoreLocation();
        if (storeLocation) {
            const routeButton = document.getElementById("btn_track_order_route");
            if (routeButton)
                routeButton.onclick = () => {
                    Util.openDirections(storeLocation.lat, storeLocation.lng);
                };
        }

        if (!this._trackerTimeoutInterval)
            this._setAutoRefreshInterval();
    }

    /**
     * Sets the auto-refresh interval for the tracker page to refresh every minute.
     * @returns {void}
     */
    private readonly _setAutoRefreshInterval = (): void => {
        this._trackerTimeoutInterval = setInterval(this.updateTrackerAsync.bind(this), 60000);
    }

    /**
     * Handles the location change event to clear the page when the page is not TRACK_ORDER
     * or render the page if the page is TRACK_ORDER.
     * @param page
     * @param orderId
     * @returns
     */
    private readonly _onLocationChangeAsync = async (orderId: number): Promise<void> => {
        if (orderId > 0)
            this._item.order.id = orderId;

        this._scrollToTop();

        if (this._item.order.id > 0 && this._data.isBrandLoaded)
            await this.updateTrackerAsync();
    }

    /**
     * Scrolls the page to the top.
     */
    private readonly _scrollToTop = (): void => {
        const divPage = document.getElementById("page_trackorder");
        if (divPage) {
            divPage.scrollIntoView();
        }
    }

    /**
     * Updates the tracker data by fetching the latest data and re-rendering the page
     * or displaying an error message if fetching tracker data fails.
     * @returns {Promise<void>}
     */
    public updateTrackerAsync = async (): Promise<void> => {
       const loader = new LoaderSpinner();
        const fetchResult = await this._fetchTrackOrderData();
        loader?.destroy();
        if (fetchResult === 0) {
            this._renderPageAsync();
            this._setEventListeners();
        } else {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                `${this._localizationService.names(
                    "TrackOrder_FetchTrackOrderData_Error"
                )}`,
                true
            ); //, () => { window.history.back(); });
        }
    }

    /**
     * Fetches the latest tracking payload for the current order and updates the local item state.
     *
     * Behavior:
     * - Uses the authenticated endpoint when the user is logged in; otherwise uses the guest endpoint.
     * - On success, mutates `this._item.order` and `this._item.storeAddressInfo`.
     *
     * @returns {Promise<number>}
     * A status code indicating the outcome:
     * - `0`  : Success; local order + store info updated.
     * - `-1` : Logged-in request succeeded but returned no order (`data.order` was null/undefined).
     * - `-2` : No data returned from the service (unexpected/failed response).
     */
    private readonly _fetchTrackOrderData = async (): Promise<number> => {
        const storeKey = this._data.getBrandProperty("StoreInfo").storeKey;

        let data;
        if (this._user?.isLoggedIn()) {
            data = await this._trackerService.fetchTrackerUserData(storeKey, this._item.order.id);
            if (data?.order == null)
                return -1;
        } else {
            data = await this._trackerService.fetchTrackerData(storeKey, this._item.order.id);
        }

        if (data) {
            this._item.order = data.order;
            this._item.storeAddressInfo = data.storeAddressInfo;
            return 0;
        }
        return -2;
    }

    /**
     * Fetches the driver's current location for the current order.
     *
     * Notes:
     * - Returns `null` if the user is not logged in (driver tracking requires authentication).
     * - Treats `{ lat: 0, lng: 0 }` as an invalid/unavailable location and returns `null`.
     *
     * @returns {Promise<LatLng | null>}
     * The driver's coordinates when available; otherwise `null`.
     */
    private readonly _fetchTrackDriverLocationData = async (): Promise<LatLng | null> => {
        const storeKey = this._item.storeAddressInfo.storeKey;
        if (!this._user?.isLoggedIn())
            return null;

        const data = await this._trackerService.fetchTrackerDriverData(storeKey, this._item.order.id);
        if (!data || (data.lat === 0 && data.lng === 0))
            return null;

        return data;
    }

    /**
     * Renders the page with the order status and location bar.
     */
    private readonly _renderPageAsync = async (): Promise<void> => {
        this._buildOrderStatusSection();
        await this._buildMapSectionAsync();

        if (!this._isRendered) {
            this._buildLocationBar();
            this._isRendered = true;
        }

        this._item.oldOrderInfo = {
            deliveryCount: this._item.order.deliveryCount,
            deliveryProviderId: this._item.order.deliveryProviderId
        };

        if (this._item.order.orderTypeId === OrderType.DELIVERY)
            Util.hideElement("btn_track_order_route")
        else
            Util.showElement("btn_track_order_route");

        Util.setElement("innerText", "div_header_center_text", this._localizationService.names("TrackOrder"));
        Util.setElement("innerText", "div_header_center_subtext", `${this._localizationService.names("Order")} ${this._localizationService.names("Number")}: ${this._item.order.id}`);
        Util.showElement("div_header_center_text");
        Util.showElement("div_header_center_subtext");
        Util.hideElement("div_order_type_address_nav_bar");

        this._applyOrderHubMode();
    }

    /**
     * When running in Order Hub mode (context.isOrderHub = true):
     * - Both views: hides the order cart icon and "Back to home" button.
     * - Mobile/App view: also hides the hamburger menu.
     * - Desktop view: also hides the footer.
     */
    private _applyOrderHubMode(): void {
        if (!this._context.getProperty("isOrderHub")) return;

        // Common to both views
        Util.hideElement("div_back_home");

        const footer = document.querySelector<HTMLElement>("footer");
        if (footer)
            Util.hideElement(footer);
    }

    /**
     * Builds the order status section with the order status header and progress bar.
     */
    private readonly _buildOrderStatusSection = (): void => {
        const parent = document.getElementById("div_order_status");
        if (!parent)
            return;

        parent.innerHTML = '';
        const activeStatusIndex = this._item.order.status.statusesInfo.filter(x => x.isCompleted).length - 1;

        //build header text
        this._buildOrderStatusHeader(parent, activeStatusIndex);
        //build progress bar
        this._buildOrderStatusBar(parent, activeStatusIndex);
    }

    /**
     * Builds (or reuses) the order-tracking map and renders the current set of map features.
     *
     * Responsibilities:
     * - Ensures the map instance exists and is attached to the map container.
     * - Clears previously rendered markers for this view.
     * - Adds the store marker and wires up its info window click behavior.
     * - Delegates order-specific rendering:
     *   - Delivery: adds destination marker, draws route/line, and may start vehicle tracking.
     *   - Non-delivery: attempts to add the user's current location marker.
     * - Adjusts the viewport to include all collected coordinates (or centers on the store only).
     *
     * Early returns:
     * - If the map container element cannot be found.
     * - If the store location cannot be resolved from the current order/store key.
     *
     * Side effects:
     * - Mutates `this._modules.mapInstance` (initialization and marker state).
     * - Adds markers/routes and may start/stop the vehicle refresh timer via delegated handlers.
     *
     * @private
     * @returns {Promise<void>} Resolves after the map has been updated for the current order state.
     */
    private async _buildMapSectionAsync(): Promise<void> {
        const mapDiv = document.getElementById("div_track_order_map");
        if (!mapDiv)
            return;

        if (this._isRendered && this._item.order.orderTypeId !== OrderType.DELIVERY)
            return;

        this._modules.mapInstance ??= await this._modules.mapProvider.createMap(
            mapDiv,
            {
                zoom: 10,
                center: { lat: 0, lng: 0 },
                disableDefaultUI: true,
                gestureHandling: "greedy",
                mapId: "Track_Order_Map"
            }
        );

        this._modules.mapInstance.resize();
        this._modules.mapInstance.clearMarkers();

        const store = this._data.locations.find(x => x.storeKey === this._item.storeAddressInfo.storeKey);
        if (!store)
            return;

        const boundsCoords: Array<LatLng> = [];
        const contentFolderUrl = this._context.getProperty("publicStorageUrl");

        // Store Pinpoint
        const storeUrl = `${contentFolderUrl}/online3/svg/location_pinpoint.svg`;
        const storeCoords = this._getStoreLocation();
        boundsCoords.push(storeCoords);

        const marker = await this._modules.mapInstance.addMarkerAsync(storeCoords, { iconUrl: storeUrl });

        marker.onClick(() => {
            const html = this._modules.mapMarkerInfoWindow.createStoreInfoWindow(store, this._context.getProperty("languageCode"));
            this._modules.mapInstance!.openInfo(marker, html);
        });

        if (this._item.order.orderTypeId === OrderType.DELIVERY)
            await this._handleDeliveryOrderMapAsync(storeCoords, boundsCoords);
        else
            await this._handleNonDeliveryOrderMapAsync(boundsCoords)

        if (boundsCoords.length === 1) {
            // Single marker fallback
            this._modules.mapInstance.setCenter(boundsCoords[0]);
        } else {
            const bounds = this._createBoundsRectFromCoords(boundsCoords);
            this._modules.mapInstance.fitBoundsRect?.(bounds);
        }
    }

    /**
     * Configures the map for a delivery order by:
     * - Adding the delivery destination marker (user pinpoint).
     * - Optionally starting driver/vehicle marker tracking and refresh.
     * - Drawing the route (or straight line) from the store to the destination.
     *
     * Side effects:
     * - Mutates `boundsCoords` by pushing the destination coordinates.
     * - Adds markers and a route to the map.
     * - Starts/stops the vehicle refresh timer and may clear the existing vehicle marker.
     *
     * @param {LatLng} storeCoords
     * The store coordinates used as the route origin.
     * @param {LatLng[]} boundsCoords
     * A collection of coordinates used later for fitting map bounds; the destination coordinates
     * will be appended when available.
     * @returns {Promise<void>}
     * Resolves when markers/routes are added and vehicle tracking has been started/stopped as needed.
     */
    private async _handleDeliveryOrderMapAsync(storeCoords: LatLng, boundsCoords: LatLng[]): Promise<void> {
        const deliveryAddress = this._item.order.address;
        if (!deliveryAddress) return;

        const mapInstance = this._modules.mapInstance!;

        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        const userUrl = `${contentFolderUrl}/online3/svg/user_pinpoint.svg`;

        const userCoords: LatLng = { lat: deliveryAddress.latitude, lng: deliveryAddress.longitude };
        boundsCoords.push(userCoords);

        await mapInstance.addMarkerAsync(userCoords, { iconUrl: userUrl });

        const order = this._item.order;
        const shouldTrackVehicle =
            order.deliveryCount <= 1 && order.deliveryProviderId === DeliveryProvider.NONE;

        if (shouldTrackVehicle) {
            await this._refreshVehicleMarkerAsync(); // initial add
            this._startVehicleRefresh();             // refresh every 25s
        } else {
            // Ensure refresh isn't running from a previous state
            this._stopVehicleRefresh();
            this._clearVehicleMarker();
        }

        if (this._isRendered && !this._orderDeliveryInfoChanged())
            return;

        const mode = shouldTrackVehicle ? "route" : "straight";
        await mapInstance.drawRouteAsync(storeCoords, userCoords, mode);
    }

    /**
     * Configures the map for a non-delivery order (e.g., pickup) by:
     * - Getting the user's current geolocation (if available).
     * - Adding a user pinpoint marker.
     *
     * Side effects:
     * - Mutates `boundsCoords` by pushing the user's current coordinates (when available).
     * - Adds a marker to the map.
     *
     * Notes:
     * - If geolocation is unavailable/denied, this method exits without changing the map.
     *
     * @param {LatLng[]} boundsCoords
     * A collection of coordinates used later for fitting map bounds; the user's location coordinates
     * will be appended when available.
     * @returns {Promise<void>}
     * Resolves when geolocation is checked and the marker is added (if possible).
     */
    private async _handleNonDeliveryOrderMapAsync(boundsCoords: LatLng[]): Promise<void> {
        const pos = await Util.getCurrentPositionAsync();
        if (!pos) return;

        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        const userUrl = `${contentFolderUrl}/online3/svg/user_pinpoint.svg`;
        const userCoords: LatLng = { lat: pos.latitude, lng: pos.longitude };
        boundsCoords.push(userCoords);

        await this._modules.mapInstance!.addMarkerAsync(userCoords, { iconUrl: userUrl });
    }

    /**
     * Creates a rectangular bounding box that encompasses all provided coordinates.
     *
     * The resulting bounds can be used to fit a map viewport so that all points
     * are visible.
     *
     * @private
     * @param {Array<LatLng>} coords - List of latitude/longitude coordinates to bound
     * @returns {BoundsRect} A bounds rectangle containing north, south, east, and west limits
     */
    private _createBoundsRectFromCoords(coords: Array<LatLng>): BoundsRect {
        return {
            north: Math.max(...coords.map(c => c.lat)),
            south: Math.min(...coords.map(c => c.lat)),
            east: Math.max(...coords.map(c => c.lng)),
            west: Math.min(...coords.map(c => c.lng))
        };
    }

    /**
     * Gets the order status text based on the status ID and delivery type.
     * @param statusID - The status ID of the order.
     * @param isDelivery - Whether the order is for delivery or pickup.
     * @returns {string} - The order status text.
     */
    private readonly _getOrderStatusText = (statusID: number, orderTypeId: number): string => {
        switch (statusID) {
            case OrderStatus.Received:
                return this._localizationService.names("WeReceivedOrder");
            case OrderStatus.Making:
            case OrderStatus.Cooking:
                return this._localizationService.names("WeArePreparingOrder");
            case OrderStatus.Ready:
                {
                    switch (orderTypeId) {
                        case OrderType.DELIVERY:
                            return this._localizationService.names("WeArePreparingOrder");
                        case OrderType.TAKE_OUT:
                            return this._localizationService.names("YourOrderReadyForPickup");
                        default:
                            return this._localizationService.names("EstimatedOrderTime");
                    }
                }
            case OrderStatus.BeingDelivered:
            case OrderStatus.OrderArriving:
                return this._localizationService.names("YourOrderOutForDelivery");
            case OrderStatus.Delivered:
                return this._localizationService.names("YourOrderDelivered");
            default:
                return "Unknown Status";
        }
    }

    /**
     * Builds the order status header with the order status text and estimated time.
     * @param parent - The parent element to append the order status header to.
     * @param activeStatusIndex - The index of the active status in the statusesInfo array.
     */
    private _buildOrderStatusHeader(parent: HTMLElement, activeStatusIndex: number): void {
        const isDelivery = this._item.order.orderTypeId === OrderType.DELIVERY;
        const activeStatus = activeStatusIndex > -1 ? this._item.order.status.statusesInfo[activeStatusIndex] : null;
        if (this._item.order.status.trackerStatusOK && activeStatus) {
            const statusId = activeStatus.statusID || 0;
            const estimatedTime = this._getCurrentWaitTime(isDelivery);

            const divOrderStatusHeader = document.createElement("div");
            divOrderStatusHeader.id = 'div_order_status_header';
            divOrderStatusHeader.classList.add("bold-text");
            divOrderStatusHeader.innerText = this._getOrderStatusText(statusId, this._item.order.orderTypeId).toUpperCase();
            parent.appendChild(divOrderStatusHeader);

            const showEstimatedTime = isDelivery ? statusId !== OrderStatus.Delivered : statusId !== OrderStatus.Ready;

            if (showEstimatedTime && estimatedTime)
                this._buildEstimatedTime(parent, this._item.order.orderTypeId, estimatedTime);

            if (this._item.order.deliveryCount > 1 || this._item.order.deliveryProviderId !== DeliveryProvider.NONE)
                this._buildDeliveryInfo(parent);
        } else {
            const scheduledMsg = this._localizationService.names("YourOrderIsScheduledFor");
            if (!scheduledMsg)
                return;

            const statusMsg = scheduledMsg.replace("{{datetime}}", `<br class="scheduledTxt">${Util.formatDateTime(this._item.order.status.estimatedPromiseTime!)}`);
            const spanScheduledMessage = document.createElement("span");
            spanScheduledMessage.id = 'span_scheduled_message';
            spanScheduledMessage.classList.add("bold-text");
            spanScheduledMessage.innerHTML = statusMsg;
            parent.appendChild(spanScheduledMessage);
        }
    }

    /**
     * Builds the estimated time text and appends it to the parent element.
     * @param parent - The parent element to append the estimated time text to.
     * @param isDelivery - Whether the order is for delivery or pickup.
     * @param estimatedTime - The estimated time string.
     */
    private _buildEstimatedTime(parent: HTMLElement, orderType: OrderType, estimatedTime: Date): void {
        const formattedTime = estimatedTime.toLocaleTimeString(Util.GetBrowserLanguage(), { hour: '2-digit', minute: '2-digit' });
        const minutesLeft = Math.round((estimatedTime.getTime() - Date.now()) / 60000);
        const formattedMinutes = this._localizationService.names("Minutes_Number").replaceAll("{{minutes}}", minutesLeft.toString());
        const formattedEtaTime = this._localizationService.names("ETA_TIME_WITH_MINUTES")
            .replaceAll("{{time}}", formattedTime)
            .replaceAll("{{formatted_minutes}}", formattedMinutes);

        let orderTypeTxt = '';
        switch (orderType) {
            case OrderType.DELIVERY:
                orderTypeTxt = this._localizationService.names("Delivery");
                break;
            case OrderType.TAKE_OUT:
                orderTypeTxt = this._localizationService.names("PickUp");
                break;
            default:
                orderTypeTxt = this._localizationService.names("Promise");
                break;
        }

        const estimatedTxt = this._localizationService.names("ETA_ORDER_TYPE_TIME_IS")
            .replaceAll("{{order_type}}", orderTypeTxt)
            .replaceAll("{{time}}", `<span id="span_estimated_time" class="bold-text">${formattedEtaTime}</span>`);

        const divEstimatedTime = document.createElement("div");
        divEstimatedTime.id = 'div_estimated_time';
        divEstimatedTime.innerHTML = estimatedTxt;
        parent.appendChild(divEstimatedTime);
    }

    /**
     * Renders a short delivery info message under the map/order UI.
     *
     * Message rules:
     * - If the order is delivered by the store (no 3rd-party provider), shows a multi-stop notice.
     * - If a 3rd-party provider is used, shows a “dispatched using {provider}” message.
     *
     * Side effects:
     * - Appends a new <div> element to the provided parent element.
     *
     * @param {HTMLElement} parent
     * The container element to append the delivery info text into.
     */
    private _buildDeliveryInfo(parent: HTMLElement): void {
        let providerName = "";
        switch (this._item.order.deliveryProviderId) {
            case DeliveryProvider.DOORDASH_DRIVE:
                providerName = "DoorDash Drive";
                break;
            case DeliveryProvider.SHIPDAY:
                providerName = "Shipday";
                break;
        }

        const text = this._item.order.deliveryProviderId === DeliveryProvider.NONE
            ? this._localizationService.names("ROUTE_MULTI_STOPS")
            : this._localizationService.names("ORDER_DISPATCHED_THIRDPARTY").replaceAll("{{provider_name}}", providerName);

        const divEstimatedTime = document.createElement("div");
        divEstimatedTime.innerText = text;
        divEstimatedTime.style.fontSize = "0.9rem";
        parent.appendChild(divEstimatedTime);
    }

    /**
     * Gets the current wait time based on the order type (delivery or pickup).
     * @param isDelivery - Whether the order is for delivery or pickup.
     * @returns {Date | null} - The current wait time string.
     */
    private readonly _getCurrentWaitTime = (isDelivery: boolean): Date | null => {
        const estDeliveryTime = this._item.order.status.estimatedDeliveryTime;
        if (isDelivery && estDeliveryTime) {
            return estDeliveryTime
        }

        return this._item.order.status.estimatedPromiseTime;
    }

    /**
     * Builds the order status bar with the status items and dotted lines.
     * @param parent - The parent element to append the order status bar to.
     * @param activeStatusIndex - The index of the active status in the statusesInfo array.
     */
    private readonly _buildOrderStatusBar = (parent: HTMLElement, activeStatusIndex: number): void => {
        const uncompleteClass = "tracker-uncompleted";
        const completedClass = "tracker-completed";
        const renderSmaller = this._item.order.status.statusesInfo.length > 4 ? "smaller" : "";
        const ulOrderStatusBarContainer = document.createElement("ul");
        ulOrderStatusBarContainer.id = 'ul_order_status_bar_container';
        ulOrderStatusBarContainer.classList.add("order-status-bar-container");
        if (renderSmaller) {
            ulOrderStatusBarContainer.classList.add(renderSmaller);
        }

        for (const [index, status] of this._item.order.status.statusesInfo.entries()) {
            let classStr = uncompleteClass;
            if (status.statusID === OrderStatus.Received) {
                classStr = "";
            }
            else if (status.isCompleted) {
                classStr = (index === activeStatusIndex) ? "" : completedClass;
            }

            let html = `
                <li id="li_status_${index}" class="order-status-item ${classStr} ${renderSmaller}">
                    <img class="bubble ${renderSmaller}" src="${status.imageURL}" alt="Notification Image for ${this._localizationService.namesJson(status.statusName)}"/>
                    <div class="bold-text status-text text ${renderSmaller} ${classStr}">${this._localizationService.namesJson(status.statusName)}</div>
                </li>
            `;

            const dottedLineClass = status.isCompleted ? completedClass : uncompleteClass;
            html += index < this._item.order.status.statusesInfo.length - 1 ? `<li class="dotted-line-container"><hr class="dotted-line dots ${renderSmaller} ${dottedLineClass}"></li>` : '';

            ulOrderStatusBarContainer.innerHTML += html;
        }

        parent.appendChild(ulOrderStatusBarContainer);
    }

    /**
     * Builds the location bar with the store address and phone number.
     */
    private readonly _buildLocationBar = async (): Promise<void> => {
        const parent = document.getElementById("div_location_bar");
        if (parent) {
            parent.innerHTML = '';
            const pinUrl = `${this._context.getProperty("publicStorageUrl")}/online3/svg/location_pinpoint.svg`;
            const locationTxt = `${this._item.storeAddressInfo.address1 ? this._item.storeAddressInfo.address1.toUpperCase() : 'UKNONWN ADDRESS'}`;

            parent.style.bottom = '0px';
            const divBackHome = document.getElementById("div_back_home");
            if (divBackHome && divBackHome.style.display != '') {
                parent.style.bottom = '80px';
            }

            let html = `
                <div class="store-location-bar"">
                    <div>
                        <img class="store-location-pin" src="${pinUrl}">
                    </div>
                    <span>${locationTxt}</span>
                </div>
            `;

            if (Util.isAppView() && this._item.storeAddressInfo.phone) {
                html += `
                    <div id="btn_call_store" class="store-call-button">
                        <div>
                            <span class="icon-call"></span>
                        </div>
                    </div>
                `;
            }

            parent.innerHTML = html;
        }
    }

    /**
     * Formats the phone number based on the country code and store information.
     * @returns {string} - The formatted phone number.
     */
    private readonly _formatPhoneNumber = (): string => {
        const phoneCountryId = this._user.getProperty("CountryCodeId") || this._data.getProperty("StoreInfo").countryId || 1;
        const phoneFormatLanguage = Util.getDefaultCultureCode(phoneCountryId, this._data.getProperty("Countries"));
        return Util.formatPhoneNumber(this._item.storeAddressInfo.phone, this._localizationService.names("PhoneFormat", phoneFormatLanguage));
    }

    /**
     * Resolves the current item's store coordinates from the loaded locations dataset.
     *
     * Finds a location entry whose `storeKey` matches the current item's `storeAddressInfo.storeKey`.
     *
     * @private
     * @returns {LatLng} The store coordinates (`{ lat, lng }`).
     */
    private _getStoreLocation(): LatLng {
        const storeInfo = this._item.storeAddressInfo;

        return {
            lat: storeInfo.latitude,
            lng: storeInfo.longitude
        };
    }

    /**
     * Clears the page by removing the order status and location bar elements and also clearing the timeout interval.
     */
    private readonly _clearPage = (): void => {
        Util.setElement("innerHTML", "div_order_status", "");
        if (this._trackerTimeoutInterval)
            clearInterval(this._trackerTimeoutInterval);
        this._trackerTimeoutInterval = null;
        this._isRendered = false;

        this._stopVehicleRefresh();
        this._clearVehicleMarker();
    }

    /**
     * Refreshes the vehicle/driver marker on the map.
     *
     * Behavior:
     * - Removes the previously rendered vehicle marker (if any).
     * - Fetches the latest driver location from the tracking API.
     * - Adds a new vehicle marker at the updated position.
     *
     * Notes:
     * - If location is unavailable, the method exits without placing a marker.
     * - The marker icon is scaled down to reduce visual prominence.
     *
     * Side effects:
     * - Mutates `this._vehicleMarker`.
     * - Adds/removes a marker on the map instance.
     *
     * @returns {Promise<void>}
     * Resolves after the vehicle marker has been updated (or skipped).
     */
    private async _refreshVehicleMarkerAsync(): Promise<void> {
        // clear old vehicle marker only
        this._clearVehicleMarker();

        const location = await this._fetchTrackDriverLocationData();
        if (!location || !this._modules.mapInstance)
            return;

        const contentFolderUrl = this._context.getProperty("publicStorageUrl");
        const vehicleIconUrl = `${contentFolderUrl}/online3/svg/driver_pinpoint.svg`;

        this._vehicleMarker = await this._modules.mapInstance.addMarkerAsync(location, {
            iconUrl: vehicleIconUrl,
            scale: 0.7
        });
    }

    /**
     * Removes the current vehicle/driver marker from the map (if present) without affecting
     * other markers or routes.
     *
     * Side effects:
     * - Removes a marker from the map instance.
     * - Sets `this._vehicleMarker` to `null`.
     */
    private _clearVehicleMarker(): void {
        if (!this._vehicleMarker) return;

        // Use whatever your provider exposes:
        this._modules.mapInstance?.removeMarker(this._vehicleMarker);

        this._vehicleMarker = null;
    }

    /**
     * Starts the periodic vehicle marker refresh timer.
     *
     * Behavior:
     * - Stops any existing refresh timer to prevent duplicate intervals.
     * - Refreshes the vehicle marker every 25 seconds by calling `_refreshVehicleMarkerAsync()`.
     *
     * Side effects:
     * - Mutates `this._vehicleRefreshTimer`.
     */
    private _startVehicleRefresh(): void {
        this._stopVehicleRefresh();

        this._vehicleRefreshTimer = setInterval(() => {
            // fire-and-forget, but safe
            void this._refreshVehicleMarkerAsync();
        }, 25_000);
    }

    /**
     * Stops the periodic vehicle marker refresh timer (if running).
     *
     * Side effects:
     * - Clears the refresh interval.
     * - Sets `this._vehicleRefreshTimer` to `null`.
     */
    private _stopVehicleRefresh(): void {
        if (this._vehicleRefreshTimer !== null) {
            clearInterval(this._vehicleRefreshTimer);
            this._vehicleRefreshTimer = null;
        }
    }

    /**
     * Determines whether the order's delivery-related fields have changed compared to the last cached snapshot.
     *
     * @returns {boolean} True if either `deliveryCount` or `deliveryProviderId` differs from the previous values; otherwise false.
     */
    private _orderDeliveryInfoChanged(): boolean {
        const { deliveryCount, deliveryProviderId } = this._item.order;
        const old = this._item.oldOrderInfo;

        return deliveryCount !== old.deliveryCount
            || deliveryProviderId !== old.deliveryProviderId;
    }
}